// Port of src/app/services/http-req.service.ts (Angular HttpClient + RxJS →
// axios + Promises). The Angular source is the spec — behavior ported
// function-by-function.
//
// STATE DECISION (verified by grepping src/app usages):
//  - The 4 signals (clearArtistFromUrl / artistFromUrl / clearSongFromUrl /
//    songFromUrl) ARE read reactively by components (nav-bar.component.ts
//    builds a computed() over artistFromUrl/songFromUrl and reacts in an
//    effect; main-content reads them inside an effect too) → ported as a small
//    Zustand store `useHttpReqStore` so React components can subscribe.
//  - `searchOrigin` is only read/written imperatively (nav-bar assigns it,
//    only this service reads it) → module-level mutable state with
//    get/setSearchOrigin().
//  - navigation dedup state lives in `./navigate-dedup` (shouldNavigate /
//    resetNavigateDedup) so it can be reset when results are cleared and unit-tested.

import type { AxiosError, AxiosProgressEvent, AxiosRequestConfig, AxiosResponse } from 'axios';
import { create } from 'zustand';
import { http, param, postUrlencoded } from './http';
import { logger } from '../shared/logging/logger';
import { assetUrl } from '../shared/asset-url';
import { environment } from '../config/environment';
import { navigate } from '../app/navigation';
import { shouldNavigate } from './navigate-dedup';
import { useGlobalStore } from '../stores/global-store';
import { useFiltersStore } from '../stores/filters-store';
import { useUserStore } from '../stores/user-store';
import { startAffiliationTimer } from './affiliation-timer';
import { getConnectedUserEmailAddress as getSpotifyConnectedUserEmailAddress } from './spotify/spotify-user-account';
import type { Filter } from '../interfaces/filter';
import type { Artwork } from '../interfaces/artwork';
import type { SearchResultsOs, Song } from '../interfaces/search-results-os';
import type { FinalSong, SongMetadata } from '../interfaces/final-song';
import type { User } from '../interfaces/user';
import type { YouTubeSuggestions } from '../interfaces/youtube-suggestions';
import type { CuratedPlaylist } from '../interfaces/curated-playlist';
import type { PrompterResult } from '../interfaces/prompter-result';
import type { ReportIssue } from '../interfaces/report-issue';

// ---------------------------------------------------------------------------
// Reactive state (Angular signals on HttpReqService) → Zustand store
// ---------------------------------------------------------------------------

export interface HttpReqState {
  clearArtistFromUrl: boolean;
  artistFromUrl: string | undefined;
  clearSongFromUrl: boolean;
  songFromUrl: string | undefined;

  setClearArtistFromUrl: (value: boolean) => void;
  setArtistFromUrl: (value: string | undefined) => void;
  setClearSongFromUrl: (value: boolean) => void;
  setSongFromUrl: (value: string | undefined) => void;
}

export const useHttpReqStore = create<HttpReqState>()((set) => ({
  clearArtistFromUrl: false,
  artistFromUrl: undefined,
  clearSongFromUrl: false,
  songFromUrl: undefined,

  setClearArtistFromUrl: (clearArtistFromUrl) => set({ clearArtistFromUrl }),
  setArtistFromUrl: (artistFromUrl) => set({ artistFromUrl }),
  setClearSongFromUrl: (clearSongFromUrl) => set({ clearSongFromUrl }),
  setSongFromUrl: (songFromUrl) => set({ songFromUrl }),
}));

// ---------------------------------------------------------------------------
// Non-reactive service fields → module-level mutable state
// ---------------------------------------------------------------------------

export type SearchOrigin =
  'photo_upload' | 'video_upload' | 'filter_click' | 'free_text' | 'url' | 'playlist_clicked';

let searchOrigin: SearchOrigin = 'url';

export function getSearchOrigin(): SearchOrigin {
  return searchOrigin;
}

export function setSearchOrigin(origin: SearchOrigin): void {
  searchOrigin = origin;
}

const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds
const defaultLargeImage = 'https://d382nvfdu38z2f.cloudfront.net/Static/Songhunt/default_large.webp';
const defaultSmallImage = 'https://d382nvfdu38z2f.cloudfront.net/Static/Songhunt/default_small.webp';

let userDetailsTimer: ReturnType<typeof setInterval> | undefined;
let abortController: AbortController | undefined;
let currentAbort: (() => void) | undefined; // Store the current abort function

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export function register(userRegistration: Record<string, any>): Promise<AxiosResponse> {
  return postUrlencoded(environment.baseUrl + 'register', userRegistration);
}

export async function getUserDetails(): Promise<User> {
  const user = await returnHttpGetRequest<User>(environment.baseUrlWithApiSh + 'auth/get-user-details');

  if (isEmailExcluded(user.email)) {
    startAffiliationTimer();
  }

  const connectedUserEmailAddress = getSpotifyConnectedUserEmailAddress();
  if (connectedUserEmailAddress && connectedUserEmailAddress === user.email) {
    user.isSpotifyConnected = true;
  }

  return user;
}

export function getSignupSvg(): void {
  const baseDomain = location.host.split('.')[0];

  http.get<any>(environment.baseUrl + 'get-signup-svg', generateHttpGetOptions({ baseDomain }))
    .then(res => {
      if (res.data && res.data.svg) {
        useGlobalStore.getState().setGlobalField('signUpSvg', res.data.svg);
      } else {
        useGlobalStore.getState().setGlobalField('signUpSvg', undefined);
      }
    })
    .catch(() => {
      useGlobalStore.getState().setGlobalField('signUpSvg', undefined);
    });
}

export async function login(username: string, password: string): Promise<any> {
  const res = await postUrlencoded<any>(environment.baseUrlWithApiSh + 'auth/login', { username, password });

  // Side effect performed when the call is successful (Angular `tap`)
  if (isEmailExcluded(username)) {
    startAffiliationTimer();
  }

  return res.data;
}

export function loginDefaultUser(): Promise<any> {
  return login(environment.defaultUserEmail, environment.defaultUserPass);
}

export async function logout(): Promise<void> {
  await http.get<void>(environment.baseUrl + 'logout', generateHttpGetOptions());
  useGlobalStore.getState().resetPlaylist();
}

export function isEmailExcluded(email: string): boolean {
  return !environment.emailsToLogout.includes(email);
}

// ---------------------------------------------------------------------------
// Filters / projects
// ---------------------------------------------------------------------------

export async function getFilters(): Promise<Filter[]> {
  const res = await returnHttpGetRequest<{ metadata: []; features: Filter[]; languages: [] }>(
    environment.baseUrlWithApiSh + 'search/list/filters');
  return res.features;
}

// when updating the filters file - rename the file
export async function getFiltersJson(): Promise<Filter[]> {
  // assetUrl() resolves against the app base. A bare relative path 404'd on
  // multi-segment routes (e.g. /songhunt/similar/<deep-link>), returning index.html
  // → `res.data.map is not a function` → startLoadingProcess rejected → results
  // never loaded for /similar and /artist share links. See src/shared/asset-url.ts.
  const res = await http.get<Filter[]>(assetUrl('assets/files/filters_v121.json'));
  return res.data.map((filter) => finalFilterMapping(filter));
}

export async function getProjectsIds(): Promise<any> {
  return await returnHttpGetRequest(environment.baseUrlWithApiSh + 'search/list/get-public-project-ids');
}

// ---------------------------------------------------------------------------
// Search / songs
// ---------------------------------------------------------------------------

export function getSongMetadata(id: string): Promise<SongMetadata> {
  return returnHttpGetRequest(`${environment.baseUrlWithApi}artwork/${id}/metadata`);
}

export function freeTextQuery(query: string): Promise<PrompterResult> {
  logger.info('search', 'prompt (free-text query)', { query });
  return returnHttpGetRequest<PrompterResult>(
    environment.baseUrlWithApi + 'free-text/find-by-query/', { query })
    .catch((httpError) => {
      // Handle or log the HTTP error here if needed
      // Then rethrow it to be caught by the specific HTTP error handler
      throw new Error(`HTTP error: ${httpError.message}`);
    });
}

export function getYouTubeSuggestions(searchStr: string): Promise<YouTubeSuggestions[]> {
  return returnHttpGetRequest<YouTubeSuggestions[]>(
    environment.baseUrlWithApiSh + 'search/list/youtube-suggestions', {
      searchStr,
      take: '4'
    });
}

export async function getSongs(searchStr: string): Promise<Artwork[]> {
  // Remove all occurrences of the character '
  const sanitizedSearchStr = searchStr.replace(/'/g, '');

  try {
    const artworks = await returnHttpGetRequest<Artwork[]>(
      `${environment.baseUrlWithApiSh}search/list/songs`, { searchStr: sanitizedSearchStr });
    return artworks.map((artwork: Artwork) => updateArtworkCoverUrl(artwork));
  } catch (error) {
    // Handle the error appropriately
    console.error('Error fetching songs:', error);
    return [];
  }
}

export function getArtwork(id: string): Promise<Artwork> {
  return returnHttpGetRequest(environment.baseUrlWithApiSh + 'search/get/song', { id });
}

export function cancelRequests(): void {
  if (currentAbort) {
    currentAbort(); // Call the abort function to cancel the request
  }
}

export function navigateWithQueryParams(forceDispatchOnNoOp = false): void {
  const location = window.location.href;
  if (location.includes('signup') || location.includes('affiliate')) {
    return;
  }
  const filtersStore = useFiltersStore.getState();
  const httpReqStore = useHttpReqStore.getState();

  const queryString = filtersStore.convertFiltersToQueryString();
  // Suppress a redundant navigation when filters are unchanged (filter-click
  // churn). resetAllToStart resets this guard when it clears results for a new
  // search, so an explicit search landing on the same filters still refetches.
  if (!shouldNavigate(queryString)) {
    // When the caller is an explicit search (constructFiltersAndNavigate), a
    // same-URL result must NOT be dropped: React Router emits no navigation, so
    // MainContent.handleQueryParams never re-runs and NavBar's reference-song /
    // filters dispatch effect (E8, keyed on updateGlobalStateSignal) never
    // re-fires — the search silently hangs (diagnosed from the 2026-07-21 log;
    // see navigate-dedup.ts). Force the global-state bump so E8 re-dispatches
    // against the current URL.
    if (forceDispatchOnNoOp) {
      logger.warn('search', 'search resolved to unchanged URL — forcing re-dispatch', { queryString });
      useGlobalStore.getState().updateGlobalState();
    }
    return;
  }

  // NOTE(port): parse with URLSearchParams (lossless) rather than a naive
  // split('&')/split('=') reducer. The reducer silently truncated any value
  // containing '=' or '&' (e.g. search text) and, with the now-encoded values
  // from convertFiltersToQueryString, this decodes each value exactly once so
  // the re-serialization below (objectToQueryString) encodes it back identically.
  const queryParams = Object.fromEntries(
    new URLSearchParams(queryString).entries()
  ) as Record<string, string | undefined>;

  // Check if the only query parameter is referenceSongIds=0
  if (Object.keys(queryParams).length === 1 && queryParams.referenceSongIds === '0') {
    return; // If true, exit the function early, avoiding the navigate call
  }

  if (httpReqStore.clearArtistFromUrl) {
    httpReqStore.setClearArtistFromUrl(false);
    filtersStore.setArtistSearchStr(httpReqStore.artistFromUrl);
    queryParams.searchInArtist = httpReqStore.artistFromUrl;

    httpReqStore.setArtistFromUrl(undefined);
    window.location.href = trimUrlForPopups(window.location.href, '/artist/') + objectToQueryString(queryParams);
    document.title = 'Songhunt: The Future of Song Search and Playlisting';
    return;
  }

  if (httpReqStore.clearSongFromUrl) {
    httpReqStore.setClearSongFromUrl(false);
    filtersStore.setMultipleArtworksIds(httpReqStore.songFromUrl);
    queryParams.referenceSongIds = httpReqStore.songFromUrl;

    httpReqStore.setSongFromUrl(undefined);
    window.location.href = trimUrlForPopups(window.location.href, '/similar/') + objectToQueryString(queryParams);
    document.title = 'Songhunt: The Future of Song Search and Playlisting';
    return;
  }

  // Navigate with the query parameters (Angular: router.navigate([''], { queryParams }))
  navigate('/', { search: objectToQueryString(queryParams) });
}

export function getQuickTags(searchInArtist?: string): Promise<FinalSong[]> {
  const globalStore = useGlobalStore.getState();
  const filtersStore = useFiltersStore.getState();

  globalStore.setSongsLoading(true);
  cancelRequests();

  const params: any = {
    pageNum: globalStore.getPageNumForCall()
  };

  if ((location.href.includes('searchInArtist=') || location.href.includes('/artist/'))
    && globalStore.artistPages !== -1) {
    params.artistPages = globalStore.artistPages;
  }

  let filters: any = {};
  let defaultSongs = false;

  if (filtersStore.constructFeaturesAndCheckIfNotEmpty()) {
    // constructFeaturesAndCheckIfNotEmpty replaced filterForApiCall — re-read it.
    filters = deepClone(useFiltersStore.getState().filterForApiCall);
    removeIdFromNonNumericMetadata(filters);

    const primaryFilterId = filtersStore.getPrimaryFilterId();
    if (primaryFilterId) {
      filters.primaryFilterId = primaryFilterId;
    } else {
      delete filters.primaryFilterId;
    }
  } else {
    defaultSongs = true;
  }

  if (searchInArtist) {
    filters.features = [];
    filters.numericMetadata = [];
    filters.nonNumericMetadata = {};
    filters.searchInArtist = searchInArtist;
  }

  filters.projectIds = getProjectIdsForRequest(globalStore.projectIdsToUse);

  if (filtersStore.tagsWithOperator.length > 0) {
    const featureMap: any = new Map(
      filters.features?.map((feature: any) => [feature.id, feature]) || []
    );

    filtersStore.tagsWithOperator.forEach((tag) => {
      const feature = featureMap.get(tag.filterId);
      if (feature) {
        feature.genresOperator = tag.genresOperator;
      }
    });
  }
  filters.origin = searchOrigin;

  params.filters = JSON.stringify(filters);
  if (defaultSongs && !searchInArtist) {
    params.defaultSongs = JSON.stringify(defaultSongs);
  }

  params.discoveryMode = globalStore.discoveryMode;

  return getSongsRequest(environment.baseUrlWithApiSh + 'search/tags/search', params);
}

export function getResultsOs(referenceSongIds: string): Promise<FinalSong[]> {
  const globalStore = useGlobalStore.getState();
  const filtersStore = useFiltersStore.getState();

  globalStore.setSongsLoading(true);
  cancelRequests();

  const params: any = { referenceSongIds, pageNum: globalStore.getPageNumForCall() };

  let filters: any = {};
  if (filtersStore.constructFeaturesAndCheckIfNotEmpty()) {
    filters = deepClone(useFiltersStore.getState().filterForApiCall);
    removeIdFromNonNumericMetadata(filters);
  }

  filters.projectIds = getProjectIdsForRequest(globalStore.whiteLabelProjectIds);

  filters.origin = searchOrigin;

  params.filters = JSON.stringify(filters);

  params.discoveryMode = globalStore.discoveryMode;

  const url = `${environment.baseUrlWithApiSh}search/song/${filtersStore.getSelectedSortAddress()}`;
  return getSongsRequest(url, params);
}

export async function getResultsOsFamilies(referenceSongIds: string, songIds: number[]): Promise<FinalSong[]> {
  const globalStore = useGlobalStore.getState();
  const filtersStore = useFiltersStore.getState();

  const params: any = { referenceSongIds, pageNum: globalStore.getPageNumForCall() };

  let filters: any = {};
  if (filtersStore.constructFeaturesAndCheckIfNotEmpty()) {
    // NOTE(port): Angular used the live (non-cloned) filterForApiCall here — preserved.
    filters = useFiltersStore.getState().filterForApiCall;
  }

  filters.projectIds = getProjectIdsForRequest(globalStore.whiteLabelProjectIds);
  filters.origin = searchOrigin;
  filters.skips = [];
  filters.ids = songIds;
  const weights = { 'lyrics_semantics': 0.2, 'lyrics_aesthetics': 0.2, 'music': 0.1, 'production': 0.5 };
  const columns = '';

  params.filters = JSON.stringify(filters);
  params.weights = JSON.stringify(weights);
  params.columns = JSON.stringify(columns);

  const urlSemantics = `${environment.baseUrlWithApiSh}search/song/semantics-alike-search`;
  const urlAesthetics = `${environment.baseUrlWithApiSh}search/song/aesthetics-alike-search`;

  try {
    const [semantics, aesthetics] = await Promise.all([
      returnHttpGetRequest<SearchResultsOs>(urlSemantics, params),
      returnHttpGetRequest<SearchResultsOs>(urlAesthetics, params)
    ]);

    const songMap: { [key: number]: Song } = {};

    const addOrUpdateSong = (song: Song) => {
      // If the song doesn't exist in songMap and its score is greater than 8.5, add it
      if (!songMap[song.id] && song.score > 8.5) {
        songMap[song.id] = song;
      } else if (songMap[song.id]) {
        // If the song already exists in songMap, update its score to the highest score
        songMap[song.id].score = Math.max(songMap[song.id].score, song.score);
      }
    };

    if (semantics) semantics.songs.forEach(addOrUpdateSong);
    if (aesthetics) aesthetics.songs.forEach(addOrUpdateSong);

    const combinedSongs: FinalSong[] = Object.values(songMap).map(song => ({
      id: song.id.toString(),
      artist: song.artist,
      title: song.title,
      url: song.url,
      imageUrl: song.imageUrl,
      youtube_id: song.youtube_id,
      large_image_url: song.large_image_url,
      webp_url: song.webp_url,
      songAddedToPlaylist: false,
      isPopular: song.isPopular,
      lyricsMatch: song.score > 8.5,
      spotify_id: song.spotify_id,
      timestamp: song.timestamp ? song.timestamp : 0
    }));

    return combinedSongs;
  } catch (error) {
    return handleError(error);
  }
}

export function constructFiltersAndNavigate(): void {
  useFiltersStore.getState().constructFeaturesAndCheckIfNotEmpty();
  // An explicit search (prompt / filter click) must always refetch — even when it
  // resolves to the query string already on screen. Pass forceDispatchOnNoOp so a
  // dedup no-op still re-fires the dispatch effect instead of silently hanging.
  navigateWithQueryParams(true);
}

export async function getSongsRequest(url: string, params: any): Promise<FinalSong[]> {
  navigateWithQueryParams();

  let parsedFilters: unknown = params?.filters;
  try {
    if (typeof params?.filters === 'string') parsedFilters = JSON.parse(params.filters);
  } catch {
    // leave parsedFilters as the raw string if it isn't valid JSON
  }
  logger.info('search', `search dispatch ${url}`, {
    filters: parsedFilters,
    pageNum: params?.pageNum,
    discoveryMode: params?.discoveryMode,
    referenceSongIds: params?.referenceSongIds,
  });

  let results: SearchResultsOs;
  try {
    // Initiating a new request
    const { promise, abort } = returnHttpGetRequestWithAbort<SearchResultsOs>(url, params);
    currentAbort = abort; // Store abort function for later cancellation
    results = await promise;
  } catch (error: any) {
    // This block should now catch AbortError (axios: CanceledError)
    if (error.name === 'AbortError' || error.name === 'EmptyError' || error.name === 'CanceledError') {
      console.log('The request was aborted');
    } else {
      console.error('Request failed:', error);
    }
    throw error;
  }

  const filteredAndMappedSongs = results!.songs
    .filter(song => emptySongVerify(song))
    .map(song => imageMapping(song));

  const uniqueSongs = makeUnique(filteredAndMappedSongs);

  // Pair every 'search dispatch' with its outcome. A "no results" / "wrong
  // results" report was previously untraceable — the log showed the request
  // but never how many songs (if any) came back for that page.
  logger.info('search', 'search results', {
    returned: results!.songs.length,
    afterFilter: uniqueSongs.length,
    pageNum: params?.pageNum,
    referenceSongIds: params?.referenceSongIds,
  });

  return uniqueSongs.map(song => finalSongMapping(song));
}

// ---------------------------------------------------------------------------
// Promo codes / checkout
// ---------------------------------------------------------------------------

export function checkPromoCode(promotionCode: string): Promise<any> {
  return returnHttpGetRequest(environment.baseUrlWithApi + 'accounting/stripe/check-promo-code', { promotionCode });
}

export function getPromoCodeFromRefCode(refCode: string, isInvited?: boolean): Promise<any> {
  if (isInvited) {
    return returnHttpGetRequest(environment.baseUrlWithApi + 'accounting/check-affiliate-price');
  }
  return returnHttpGetRequest(environment.baseUrlWithApi + 'accounting/check-affiliate-price', { refCode });
}

export function createCheckoutSession(promotionCode: string | null): void {
  const body = promotionCode
    ? { productId: 5, promotionCode }
    : { productId: 5 };

  http.post<any>(environment.baseUrlWithApi + 'accounting/stripe/create-checkout-session', param(body), {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    withCredentials: true
  }).then((res) => {
    window.location.href = res.data.url;
    // initiateGetUserDetailsTimeout();
  });
}

// ---------------------------------------------------------------------------
// Misc content
// ---------------------------------------------------------------------------

export async function submitSong(song: FinalSong): Promise<AxiosResponse<any>> {
  const title = song.title;
  const artist = song.artist;
  const lyrics = song.lyrics || '';
  const coverUrl = song.imageUrl;
  const youtubeLink = `https://www.youtube.com/watch?v=${song.youtube_id}`;
  const projectId = 3000;

  const res = await postUrlencoded<any>(environment.baseUrlWithApi + 'artwork/create',
    { title, artist, lyrics, coverUrl, youtubeLink, projectId });

  return postUrlencoded<any>(environment.baseUrlWithApiSh + 'search/trigger-processing',
    { artworkId: res.data.id });
}

export function getCuratedPlaylists(): Promise<CuratedPlaylist[]> {
  return returnHttpGetRequest(environment.baseUrlWithApi + 'get-curated-playlists');
}

export function trimUrlForPopups(url: string, valueToTrim: string): string {
  // Check if the URL contains '/playlist/ | /artists/'
  const index = url.indexOf(valueToTrim);
  if (index !== -1) {
    // Trim the URL from '/playlist/' onward
    return url.substring(0, index);
  }

  // If valueToTrim not found, also check for existing query parameters and remove them
  // This handles the case where we're already on a results page with query params
  const queryIndex = url.indexOf('?');
  if (queryIndex !== -1) {
    return url.substring(0, queryIndex);
  }
  return url; // Return the original URL if '/playlist/' is not found
}

/**
 * Port of the Angular `uploadFile` (reportProgress events → onUploadProgress
 * callback). 2-minute timeout; timeout and network errors are mapped to the
 * same user-facing Error messages.
 */
export async function uploadFile(
  uploadUrl: string,
  formData: FormData,
  onUploadProgress?: (event: AxiosProgressEvent) => void
): Promise<AxiosResponse> {
  try {
    return await http.post(uploadUrl, formData, {
      withCredentials: true,
      timeout: 2 * 60 * 1000, // 2 minute timeout
      onUploadProgress
    });
  } catch (error: any) {
    const axiosError = error as AxiosError;
    if (axiosError.code === 'ECONNABORTED' || axiosError.code === 'ETIMEDOUT') {
      throw new Error('Upload timed out. Please try again.');
    }
    if (!axiosError.response && axiosError.isAxiosError) {
      throw new Error('Network error occurred. Please check your connection and try again.');
    }
    throw error;
  }
}

// ---------------------------------------------------------------------------
// User preferences
// ---------------------------------------------------------------------------

export async function updateUserPreference(key: string, value: string): Promise<AxiosResponse<any>> {
  const res = await postUrlencoded<any>(environment.baseUrlWithApi + 'update-preferences', { [key]: value });

  const userStore = useUserStore.getState();
  const user = userStore.user!;
  user.preferences[key] = value;
  userStore.setUser(user);

  return res;
}

export async function updateUserPreferences(
  preferences: Array<{ key: string; value: string }>
): Promise<AxiosResponse<any>> {
  const params = preferences.reduce((acc, { key, value }) => ({ ...acc, [key]: value }), {});

  const res = await postUrlencoded<any>(environment.baseUrlWithApi + 'update-preferences', params);

  const userStore = useUserStore.getState();
  const user = userStore.user!;
  preferences.forEach(({ key, value }) => {
    user.preferences[key] = value;
  });
  userStore.setUser(user);

  return res;
}

// ---------------------------------------------------------------------------
// Reporting / events
// ---------------------------------------------------------------------------

export function sendArtworkIssueReport(reportIssue: ReportIssue): Promise<AxiosResponse<any>> {
  return postUrlencoded<any>(environment.baseUrl + 'report/artwork-issue', reportIssue as unknown as Record<string, any>);
}

export function sendEventToServer(eventName: string, objOrString: string | object): void {
  const processedProperties = typeof objOrString === 'string'
    ? convertObjectToKeyMapString({ objOrString })
    : convertObjectToKeyMapString({ properties: JSON.stringify(objOrString) });

  const body = new URLSearchParams({
    eventName,
    properties: processedProperties
  });

  http.post<any>(
    environment.baseUrlWithApi + 'events/add-event',
    body.toString(),
    {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      withCredentials: true,
      loaderIgnored: true // Angular: NGX_LOADING_BAR_IGNORED context
    }
  ).then(() => {
    // Handle success or leave empty
  }).catch(() => {
    // Angular subscribed without an error handler — swallow.
  });
}

// ---------------------------------------------------------------------------
// Private helpers (Angular private methods → module-private functions)
// ---------------------------------------------------------------------------

function objectToQueryString(params: Record<string, any>): string {
  // Create an instance of URLSearchParams
  const searchParams = new URLSearchParams();

  // Loop through the object and append each key-value pair to the searchParams
  // Ensure each value is converted to a string to avoid TypeScript errors
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined) {
      searchParams.append(key, String(value));
    }
  }

  // Return the query string that starts with '?'
  return '?' + searchParams.toString();
}

function updateArtworkCoverUrl(artwork: Artwork): Artwork {
  const invalidCoverUrls = ['nan', 'assets/img/', 'NA'];
  const isInvalidUrl = invalidCoverUrls.some(invalidUrl => artwork.coverUrl?.includes(invalidUrl));

  if (!artwork.coverUrl || isInvalidUrl) {
    artwork.coverUrl = defaultSmallImage;
  }

  return artwork;
}

// NOTE(port): unused in Angular too (the only call site, in
// createCheckoutSession, is commented out). Kept for parity.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function initiateGetUserDetailsTimeout(): void {
  // If there's an existing timer, clear it first
  if (userDetailsTimer) {
    clearInterval(userDetailsTimer);
    userDetailsTimer = undefined;
  }

  const startTime = Date.now();

  const tick = async () => {
    // Stop the timer if 5 minutes have passed
    if (Date.now() - startTime >= fiveMinutes) {
      if (userDetailsTimer) {
        clearInterval(userDetailsTimer);
        userDetailsTimer = undefined;
      }
      return;
    }

    try {
      const user = await getUserDetails();
      if (user.isSubscribed) {
        if (userDetailsTimer) {
          clearInterval(userDetailsTimer);
          userDetailsTimer = undefined;
        }
        useUserStore.getState().setUser(user);
      }
    } catch {
      // ignore polling errors (Angular subscription had no error handler)
    }
  };

  void tick(); // timer(0, 30000): first emission immediately
  userDetailsTimer = setInterval(() => void tick(), 30000);
}

function convertObjectToKeyMapString(obj: any): string {
  const keyValuePairs = Object.entries(obj); // get an array of the object's key-value pairs

  // concatenate the key-value pairs into a single string
  return keyValuePairs.map(([key, value]) => `${key}: ${value}`).join(', ');
}

function handleError(error: any): any {
  console.error(error); // Log the error to the console (or send to server logs)
  return { error: 'An error occurred while processing your request.' } as any;
}

function emptySongVerify(song: Song | FinalSong): boolean {
  if (useUserStore.getState().isUserWhiteLabel) {
    return Boolean(song.url && song.url.trim() !== '') &&
      Boolean(song.title && song.title.trim() !== '') &&
      Boolean(song.artist && song.artist.trim() !== '');
  }

  return Boolean(song.url && song.url.trim() !== '') &&
    Boolean(song.title && song.title.trim() !== '') &&
    Boolean(song.artist && song.artist.trim() !== '') &&
    Boolean(song.youtube_id);
}

function imageMapping<T extends Song | FinalSong>(song: T): T {
  let final_src;
  let defaultImage;

  if ('cover_url' in song) { // If song has a cover_url property
    final_src = song.webp_url || song.large_image_url || song.cover_url || song.imageUrl || defaultLargeImage;
    defaultImage = song.cover_url;
  } else {
    final_src = song.webp_url || song.large_image_url || song.imageUrl || defaultLargeImage;
  }

  if (shouldGetRandomImage(final_src)) {
    final_src = defaultLargeImage;
  }

  return { ...song, imageUrl: final_src, defaultImage: defaultImage } as T;
}

function shouldGetRandomImage(src: string): boolean {
  return src.includes('assets') || src.includes('default_cover.png') || src === 'nan';
}

function makeUnique(songs: Song[]): Song[] {
  return songs.reduce((unique: Song[], song: Song) => {
    if (!unique.find(s => (s.title === song.title && s.artist === song.artist) || s.youtube_id === song.youtube_id)) {
      unique.push(song);
    }
    return unique;
  }, []);
}

function finalFilterMapping(filter: Filter): Filter {
  filter.totalActiveFilters = 0;
  filter.filters.forEach((f) => f.state = 'none');
  return filter;
}

function finalSongMapping(song: Song): FinalSong {
  return {
    id: song.id.toString(),
    artist: song.artist,
    title: song.title,
    url: song.url,
    imageUrl: song.imageUrl,
    youtube_id: song.youtube_id,
    large_image_url: song.large_image_url,
    webp_url: song.webp_url,
    isPopular: song.isPopular,
    songAddedToPlaylist: false,
    lyrics: song.lyrics,
    defaultImage: song.defaultImage,
    spotify_id: song.spotify_id,
    timestamp: song.timestamp ? song.timestamp : 0
  };
}

async function returnHttpGetRequest<T = any>(url: string, params?: Record<string, any>): Promise<T> {
  const res = await http.get<T>(url, generateHttpGetOptions(params));
  return res.data;
}

function returnHttpGetRequestWithAbort<T = any>(url: string, params?: Record<string, any>): {
  promise: Promise<T>;
  abort: () => void;
} {
  // Cancel the previous request if it exists
  if (abortController) {
    abortController.abort(); // This will cancel the ongoing request
  }

  // Create a new AbortController instance for this request
  abortController = new AbortController();
  const controller = abortController;

  const promise = http.get<T>(url, { ...generateHttpGetOptions(params), signal: controller.signal })
    .then(res => res.data);

  // Return both the promise and an abort function
  return {
    promise,
    abort: () => controller.abort()
  };
}

function generateHttpGetOptions(params?: Record<string, any>): AxiosRequestConfig {
  return {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
    params
  };
}

// TEMPORARY: when a WL user selects a Catalog Tier sub-filter, override the projectIds with the
// hardcoded list for that tier regardless of state (green/red). When multiple tiers are selected,
// their lists are merged (deduped). Stored as numbers; each call site formats to its own wire shape.
const catalogTierProjectIdOverrides: Record<number, number[]> = {
  6001: [67, 328, 371, 372, 373, 375, 422, 438, 445, 463, 496, 498, 508], // Majors
  6002: [66, 103, 276, 374, 376, 379, 419, 420, 421, 462], // Medium Size & Song Funds
  6003: [115, 302, 304, 380, 385, 387, 388, 389, 390, 392, 393, 403, 404, 407, 408, 426, 434, 435, 486, 488, 489, 490, 491, 494, 495, 497, 506], // One Stop / Indie Catalogs / Sync Agents
  6004: [523], // Modus Media
};

function getProjectIdsForRequest<T extends string | number[]>(fallback: T): T {
  if (!useUserStore.getState().isUserWhiteLabel) return fallback;
  const catalogTier = useFiltersStore.getState().selectedFilters['Catalog Tier'];
  const selectedTiers = catalogTier?.filters.filter(sf => catalogTierProjectIdOverrides[sf.id]) ?? [];
  if (selectedTiers.length === 0) {
    logger.info('search', 'catalog tier: WL user, no tier selected → fallback projectIds', { fallback });
    return fallback;
  }
  const merged = Array.from(new Set(selectedTiers.flatMap(sf => catalogTierProjectIdOverrides[sf.id])));
  logger.info('search', 'catalog tier: override applied to projectIds', {
    selectedTierIds: selectedTiers.map(sf => sf.id),
    projectIds: merged,
  });
  return (typeof fallback === 'string' ? merged.join(',') : merged) as T;
}

function removeIdFromNonNumericMetadata(filters: any): void {
  // Angular wrapped this in ngZone.runOutsideAngular — in React we just run it.
  if (!filters.nonNumericMetadata) return;

  // Use Object.keys for better performance than for...in with hasOwnProperty
  const keys = Object.keys(filters.nonNumericMetadata);
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    const metadataArray = filters.nonNumericMetadata[key];

    // Process the array if it exists
    if (Array.isArray(metadataArray)) {
      for (let j = 0; j < metadataArray.length; j++) {
        if (metadataArray[j].id !== undefined) {
          delete metadataArray[j].id;
        }
      }
    }
  }
}

/**
 * Creates a deep copy of an object with improved performance
 * @param obj The object to clone
 * @returns A deep copy of the input object
 */
function deepClone(obj: any): any {
  // Handle null, undefined, and primitive types
  if (obj === null || obj === undefined || typeof obj !== 'object') {
    return obj;
  }

  // Use structured clone API for better performance when available
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(obj);
    } catch (e) {
      // Fallback to manual implementation if structuredClone fails
      // (e.g., for objects with functions or circular references)
    }
  }

  // Fallback to optimized manual implementation
  if (Array.isArray(obj)) {
    // Pre-allocate array for better performance
    const copy = new Array(obj.length);
    for (let i = 0; i < obj.length; i++) {
      copy[i] = deepClone(obj[i]);
    }
    return copy;
  } else {
    const copy: { [key: string]: any } = {};
    // Use Object.keys for better performance than for...in with hasOwnProperty
    const keys = Object.keys(obj);
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      copy[key] = deepClone(obj[key]);
    }
    return copy;
  }
}
